import React, { FC, useContext, useEffect, useState } from "react";
import {
  Button,
  CardContent,
  Box,
  createStyles,
  Grid,
  makeStyles,
  Slider,
  Typography,
  useMediaQuery,
  Theme,
  Divider,
  Radio,
  IconButton,
} from "@material-ui/core";
import { withStyles } from "@material-ui/styles";

import { useStyles } from "../Styles/LockTokenStyles";
import { GetSubscriptionPlans } from "../../store/Subscription/SubscriptionFunction";
import AlertContext from "../../store/Alert/AlertContext";
import { LockInputProps } from "../../interfaces";
import WalletContext from "../../store/Wallet/WalletContext";
import BuyGainModel from "../Modals/BuyGainModel";
import StakeModal from "../Modals/StakeModal";
import { MdClose, MdLock } from "react-icons/md";

import { appConfig, connectorChainLocalStorageKey } from "../../config";
import { useAccount, useConfig } from "wagmi";
import { useWalletConnect, useWeb3 } from "../../services/web3/hooks/useWeb3";
import { TokenDollar } from "../../utils/currency/usd";
import SubscriptionContext from "../../store/Subscription/SubscriptionContext";
import theme from "../../helpers/theme";
import { getLockSupportedChainsWithInfo } from "../../services/web3/constants/chains";
import LockTierStepper from "./LockTierStepper";
import { switchChain } from "@wagmi/core";

const thumbStyles = makeStyles(() =>
  createStyles({
    thumb: {
      "& .MuiSlider-thumb": {
        height: 20,
        width: 20,
        color: "#3EB8AA",
      },
      "& .MuiSlider-markActive": {
        backgroundColor: "#3EB8AA !important",
        opacity: 1,
      },
      "& .MuiSlider-mark": {
        height: "1rem",
        width: ".5rem",
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        marginTop: "-0.25rem",
        borderRadius: "100px",
      },
    },
  })
);

const sliderStyles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      color: "#3EB8AA",

      [`@media (max-width: 959px)`]: {
        marginTop: "2rem",
      },
    },
    track: {
      height: 8,
      borderRadius: 8,
    },
    rail: {
      height: 8,
      borderRadius: 4,
      opacity: 0.5,
      backgroundColor: "#d3d3d3",
    },
    valueLabel: {
      background: "rgba(65, 89, 87, 1)",
      color: "#fff",
      boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.1)",
      width: "3.25rem",
      textAlign: "center",
      display: "flex",
      justifyContent: "center",
      borderRadius: "100px",
      maxHeight: "1.75rem",
      "& *": {
        background: "transparent",
        fontSize: "12px",
        fontWeight: 700,
        [theme.breakpoints.down("sm")]: {
          fontSize: "9px",
        },
      },
    },
  });

const GainsSlider = withStyles(sliderStyles)(Slider);

const durationsWithBonus = [
  {
    duration: 3,
    bonus: 5,
  },
  {
    duration: 6,
    bonus: 10,
  },
  {
    duration: 12,
    bonus: 20,
  },
];

const LockStakingInput: FC<LockInputProps> = ({
  gainsBalance,
  createSubscription,
  inputRef,
  showStakeModalInfo = false,
  toggleModal,
  totalTokens,
  duration,
  setTotalTokens,
  setDuration,
}) => {
  const classes = useStyles();
  const thumb = thumbStyles();
  const isActive = useMediaQuery(theme.breakpoints.down("sm"));

  const config = useConfig();

  const { setAlert } = useContext<any>(AlertContext);
  const { gainsTokenModal } = useContext<any>(WalletContext);
  const { lockedGains, lockedGainsPad } = useContext<any>(SubscriptionContext);

  const { chainId } = useWeb3();
  const { address: account } = useAccount();
  const { openConnectModal } = useWalletConnect();

  const [open, setOpen] = useState(false);
  const [lockAmount, setLockAmount] = useState<number>(0);
  const [stakeAmount, setStakeAmount] = useState<number>(0);
  const [openStake, setOpenStake] = useState(false);
  const [invalidChain, toggleInvalidChain] = useState<boolean>(false);

  useEffect(() => {
    if (!chainId) return;
    const lockChains = appConfig.supportChainIds.gainsLock?.split(",");
    if (!lockChains?.includes(chainId.toString())) toggleInvalidChain(true);
    else toggleInvalidChain(false);
  }, [chainId]);

  useEffect(() => {
    setLockAmount(lockedGainsPad);
  }, [lockedGainsPad]);

  useEffect(() => {
    setTotalTokens(stakeAmount);
  }, [stakeAmount]);

  const handleSwitchNetwork = (chainId: number) => {
    switchChain(config, {
      chainId: chainId,
    })
      .then(() => {
        console.info("Successfully switched network");
      })
      .catch((error: any) => {
        setAlert(
          "Unable to switch network, please switch network manually on the wallet to continue",
          "error"
        );
      });
  };

  const handleClick = (e: any) => {
    e.preventDefault();
    if (!account && openConnectModal) {
      openConnectModal();
      return;
    }
    if (gainsBalance < 250 || stakeAmount > gainsBalance) {
      setOpen(true);
      gainsTokenModal();
      return;
    }
    if (totalTokens > 0) {
      setOpenStake(true);
    }
  };

  const setLockAndStakeAmount = (amount: number) => {
    if (amount < lockedGainsPad) {
      setLockAmount(lockedGainsPad);
      setStakeAmount(0);
      return;
    }
    setLockAmount(amount);
    setStakeAmount(amount - lockedGainsPad);
  };

  const handleAmountChange = (event: any, newValue: number | number[]) => {
    const lockAmount = newValue as number;
    if (lockAmount < lockedGainsPad) return;
    setLockAmount(lockAmount);
    setStakeAmount(lockAmount - lockedGainsPad);
  };

  const handleDurationChange = (duration: number) => {
    setDuration(duration as number);
  };

  const getIconComponent = (icon: React.ElementType | undefined) => {
    const IconComponent = icon ? React.createElement(icon) : null;
    return IconComponent;
  };

  return (
    <>
      <Grid
        item
        justifyContent="center"
        alignItems="center"
        lg={7}
        md={6}
        sm={12}
        xs={12}
        style={{
          padding: isActive ? 0 : "1.875rem 1rem 0 1.875rem",
        }}
      >
        <CardContent>
          <Grid item>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  width: "100%",
                }}
              >
                <Typography gutterBottom className={classes.stakeModalTitle}>
                  Stake GAINS
                </Typography>
                {isActive && (
                  <IconButton
                    edge="end"
                    color="inherit"
                    aria-label="close"
                    onClick={(event) => toggleModal?.(false)}
                    style={{
                      padding: "0px 12px",
                    }}
                  >
                    <MdClose />
                  </IconButton>
                )}
              </Box>
              <Divider
                style={{
                  width: "calc(100% + 4.875rem)",
                  margin: "1rem -2rem 1rem -2.875rem",
                }}
              />
              <Typography
                variant="h4"
                component="h4"
                gutterBottom
                className={classes.stakeModalTitle}
                style={{
                  fontSize: "1rem",
                  marginBottom: isActive ? ".5rem" : "3rem",
                }}
              >
                Choose Your Tier
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <GainsSlider
              min={0}
              max={100000}
              step={250}
              className={thumb.thumb}
              value={lockAmount}
              onChange={handleAmountChange}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => TokenDollar(Math.round(value))}
              getAriaValueText={(value) => TokenDollar(Math.round(value))}
            />
          </Grid>
          <Grid item alignItems="center">
            <LockTierStepper
              lockAmount={stakeAmount + lockedGains}
              setLockAndStakeAmount={setLockAndStakeAmount}
            />
          </Grid>
          <Divider
            style={{
              width: "calc(100% + 4.875rem)",
              margin: "1rem -2rem 1rem -2.875rem",
            }}
          />
          <Grid item>
            <Typography
              variant="h4"
              component="h4"
              gutterBottom
              className={classes.stakeModalTitle}
              style={{ fontSize: "1rem" }}
            >
              Lock Duration
            </Typography>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              maxWidth={isActive ? "100%" : "90%"}
            >
              {durationsWithBonus.map((time, index) => (
                <Box>
                  <Radio
                    key={index}
                    id={time.duration.toString()}
                    name="duration"
                    value={time.duration}
                    className={classes.durationInput}
                    checked={duration === time.duration}
                    onChange={() => handleDurationChange(time.duration)}
                  />
                  <Typography className={classes.durationLabel}>
                    {time.duration > 1
                      ? time.duration + " months"
                      : time.duration + " month"}
                  </Typography>
                  <Typography className={classes.bonusLabel}>
                    {" "}
                    {`${time.bonus}% bonus`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Divider
            style={{
              width: "calc(100% + 4.875rem)",
              margin: "1rem -2rem 1rem -2.875rem",
            }}
          />
          <Grid item>
            <Typography
              variant="h4"
              component="h4"
              gutterBottom
              className={classes.stakeModalTitle}
              style={{ fontSize: "1rem" }}
            >
              Select Network
            </Typography>

            <Box>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                spacing={1}
                xs={12}
              >
                {getLockSupportedChainsWithInfo().map((chainInfo, index) => (
                  <Grid item key={index}>
                    <Button
                      classes={{ label: "fs12" }}
                      variant={
                        Number(chainId) === chainInfo.chainId
                          ? "contained"
                          : "outlined"
                      }
                      color="secondary"
                      size="small"
                      startIcon={getIconComponent(chainInfo.icon)}
                      disabled={!account}
                      onClick={async () => {
                        localStorage.setItem(
                          connectorChainLocalStorageKey,
                          chainInfo.chainId.toString()
                        );
                        handleSwitchNetwork(chainInfo.chainId);
                      }}
                    >
                      {chainInfo.name}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
          <Grid item>
            <Box
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              bgcolor={isActive ? "rgba(249, 250, 251, 0.8)" : "none"}
              position={isActive ? "fixed" : "static"}
              width={isActive ? "calc(100% - 2rem)" : "100%"}
              bottom={0}
              left={0}
              padding={isActive ? "1rem" : 0}
              zIndex={200}
            >
              <Button
                size={"large"}
                onClick={handleClick}
                className={classes.stakingBtn}
                disabled={
                  account &&
                  (invalidChain ||
                    (gainsBalance > stakeAmount && totalTokens === 0))
                }
              >
                {" "}
                <MdLock style={{ marginRight: "10px" }} />
                {!account
                  ? "Connect Wallet"
                  : invalidChain
                  ? "Switch Network"
                  : gainsBalance < 250 || stakeAmount > gainsBalance
                  ? "Buy"
                  : "Stake"}{" "}
                {gainsBalance < 250 || !account || stakeAmount > gainsBalance
                  ? ""
                  : totalTokens}{" "}
                {invalidChain || !account ? "" : "GAINS"}
              </Button>
            </Box>
          </Grid>

          {!isActive && (
            <Grid item>
              {!account ? (
                <Box
                  textAlign="center"
                  display="flex"
                  justifyContent="center"
                  height="1.2rem"
                >
                  <Typography
                    variant="caption"
                    color="error"
                    style={{
                      display: "flex",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    Connect wallet then stake to subscribe. Token are
                    redeemable.
                  </Typography>
                </Box>
              ) : stakeAmount > gainsBalance && totalTokens > 0 ? (
                <Box
                  textAlign="center"
                  display="flex"
                  justifyContent="center"
                  height="1.2rem"
                >
                  <Typography
                    variant="caption"
                    color="error"
                    style={{
                      display: "flex",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    {`You have ${TokenDollar(
                      Math.round(gainsBalance)
                    )} GAINS. Buy an additional ${TokenDollar(
                      Math.round(stakeAmount - gainsBalance)
                    )} GAINS to stake.`}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box height="1.2rem" />
                </>
              )}
            </Grid>
          )}
        </CardContent>
      </Grid>
      <StakeModal
        open={openStake}
        setOpen={setOpenStake}
        createSubscription={createSubscription}
        totalTokens={totalTokens}
        displayInfo={showStakeModalInfo}
        duration={duration}
      />
      {open && <BuyGainModel open={open} setOpen={setOpen} />}
    </>
  );
};

export default LockStakingInput;
